import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  useTheme,
  useMediaQuery,
  LinearProgress,
  ListItemButton,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Box,
} from "@mui/material";
import {
  Menu,
  LayoutDashboard,
  Compass,
  Map as MapIcon,
  Printer,
  Heart,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  Bell,
  DoorOpen,
  Settings, // Added Settings icon
} from "lucide-react";

import "./DashHeader.css";
import { UserContext } from "../../context/UserContext";
import AvatarMenu from "./AvatarMenu";
import useGetEventSettings from "../../hooks/useGetEventSettings";
import useApiEp from "../../../hooks/useApiEp";
import { USER_ROLES } from "../../../constants/constants"; // Added USER_ROLES import

const VisitorHeader = () => {
  const theme = useTheme();
  const { data: eD } = useGetEventSettings();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const { user, loading } = useContext(UserContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const { data: notificationsCount } = useApiEp(
    `/user/my-notifications-unseen-count`
  );

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleExploreClick = () => {
    setExploreOpen(!exploreOpen);
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <div
      className="expp-dash-header-c"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 16px",
      }}
    >
      {isLargeScreen ? (
        <Link to={`/${user.role}`} style={{ textDecoration: "none" }}>
          {eD && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 1 }}>
              <img
                src={eD?.eventLogo}
                alt="Event Logo"
                style={{
                  height: 50,
                  display: "block",
                  cursor: "pointer",
                  objectFit: "contain",
                }}
              />
              {eD?.eventName && (
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1e293b",
                    fontWeight: 600,
                    letterSpacing: "-0.5px",
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {eD.eventName}
                </Typography>
              )}
            </Box>
          )}
        </Link>
      ) : (
        <IconButton
          onClick={toggleDrawer(true)}
          color="primary"
          aria-label="menu"
          size="large"
        >
          <Menu size={24} />
        </IconButton>
      )}

      <div style={{ flex: 1 }}></div>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mr: { xs: 0, md: 2 } }}
      >
        <Link to={`/${user.role}/chat`}>
          <IconButton
            sx={{
              backgroundColor: "#f1f5f9",
              color: "#3b82f6",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#e0e7ff",
                transform: "scale(1.05)",
              },
              width: 42,
              height: 42,
            }}
          >
            <MessageSquare size={20} />
          </IconButton>
        </Link>
        <Link to={`/${user.role}/my-notifications`}>
          <IconButton
            sx={{
              backgroundColor: "#f1f5f9",
              color: "#0ea5e9",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#e0f2fe",
                transform: "scale(1.05)",
              },
              width: 42,
              height: 42,
            }}
            aria-label="notifications"
          >
            <Badge
              badgeContent={notificationsCount ? notificationsCount.count : 0}
              color="error"
              sx={{ "& .MuiBadge-badge": { fontWeight: 700 } }}
            >
              <Bell size={20} />
            </Badge>
          </IconButton>
        </Link>

        {/* Render the AvatarMenu */}
        <AvatarMenu />
      </Stack>

      {/* Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 260, pt: 2 }}>
          <List>
            <ListItemButton
              component={Link}
              to="/visitor"
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>
                <LayoutDashboard size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>

            <ListItemButton onClick={handleExploreClick}>
              <ListItemIcon>
                <Compass size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Explore" />
              {exploreOpen ? (
                <ChevronUp size={20} color="#64748b" />
              ) : (
                <ChevronDown size={20} color="#64748b" />
              )}
            </ListItemButton>

            <Collapse in={exploreOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  component={Link}
                  to="/visitor/explore/exhibitors"
                  onClick={toggleDrawer(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary="Exhibitors" />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to="/visitor/explore/products"
                  onClick={toggleDrawer(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary="Products" />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to="/visitor/explore/exhibitor-matching"
                  onClick={toggleDrawer(false)}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary="B2B AI Matchmaking" />
                </ListItemButton>
              </List>
            </Collapse>

            <ListItemButton
              component={Link}
              to="/visitor/floor-plan"
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>
                <MapIcon size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Floor Plan" />
            </ListItemButton>

            <ListItemButton
              component={Link}
              to="/visitor/meetings"
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>
                <DoorOpen size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Meetings" />
            </ListItemButton>

            <ListItemButton
              component={Link}
              to="/visitor/favorites"
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>
                <Heart size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Favorites" />
            </ListItemButton>

            {/* Privacy Settings conditionally rendered for TRADE_DELEGATE */}
            {user?.role === USER_ROLES.TRADE_DELEGATE && (
              <ListItemButton
                component={Link}
                to="/visitor/privacy-settings"
                onClick={toggleDrawer(false)}
              >
                <ListItemIcon>
                  <Settings size={20} color="#64748b" />
                </ListItemIcon>
                <ListItemText primary="Privacy Settings" />
              </ListItemButton>
            )}

            <ListItemButton
              component={Link}
              to="/visitor/print-badge"
              onClick={toggleDrawer(false)}
            >
              <ListItemIcon>
                <Printer size={20} color="#64748b" />
              </ListItemIcon>
              <ListItemText primary="Print Badge" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </div>
  );
};

export default VisitorHeader;
